import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Megaphone, Send, Trash2, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const when = iso => {
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d) / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
};

export function LeadUpdates({ leadId }) {
  const [items, setItems] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => API.get(`/leads/${leadId}/activities`).then(r => setItems(r.data.filter(a => a.activity_type === 'update'))).catch(() => setItems([])), [leadId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await API.post(`/leads/${leadId}/activities`, { activity_type: 'update', description: text.trim() }); setText(''); setOpen(false); load(); }
    catch { toast.error('Could not save update'); }
    setBusy(false);
  };
  const remove = async (id) => {
    try { await API.delete(`/leads/${leadId}/updates/${id}`); setItems(x => x.filter(a => a.id !== id)); } catch { toast.error('Could not delete'); }
  };

  return (
    <Card className="h-[280px] self-start bg-white border-gray-200" data-testid="lead-updates">
      <CardContent className="flex h-full flex-col gap-3 p-5">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900"><Megaphone className="h-4 w-4 text-[#0073ea]" /> Updates {items?.length ? <span className="text-xs font-normal text-gray-500">({items.length})</span> : null}</h3>
          <Button size="sm" variant="outline" onClick={() => setOpen(o => !o)} className="h-7 px-2 text-xs" data-testid="lead-update-toggle-btn">
            {open ? <><X className="h-3.5 w-3.5 mr-1" />Cancel</> : <><Plus className="h-3.5 w-3.5 mr-1" />Add Update</>}
          </Button>
        </div>
        {open ? (
          <div className="flex flex-1 flex-col gap-2">
            <Textarea autoFocus value={text} onChange={e => setText(e.target.value)} onKeyDown={e => (e.metaKey || e.ctrlKey) && e.key === 'Enter' && add()}
              placeholder="What's new? e.g. Seller countered at $310k, wants 30-day close" className="flex-1 resize-none bg-white border-gray-300 text-sm" data-testid="lead-update-input" />
            <Button size="sm" onClick={add} disabled={busy || !text.trim()} className="w-full bg-[#0073ea] hover:bg-[#0060c2] text-white" data-testid="lead-update-add-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1.5" />Post Update</>}
            </Button>
          </div>
        ) : (
          <div className="min-h-0 flex-1 snap-y snap-mandatory space-y-2 overflow-y-auto pr-1" data-testid="lead-updates-list">
            {items === null ? null : items.length === 0 ? <p className="py-3 text-center text-xs text-gray-500">No updates yet</p> : items.map(u => (
              <div key={u.id} className="group snap-start rounded-lg border border-gray-100 bg-gray-50 px-3 py-2" data-testid={`lead-update-${u.id}`}>
                <div className="flex items-start gap-2">
                  <p className="flex-1 whitespace-pre-wrap break-words text-sm text-gray-800">{u.description}</p>
                  <button onClick={() => remove(u.id)} className="p-1 -m-1 text-gray-400 opacity-100 sm:opacity-0 transition-opacity group-hover:opacity-100 hover:text-red-500" data-testid={`lead-update-delete-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                <p className="mt-1 text-[10px] text-gray-500">{when(u.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
